/**
 * Purchasing domain service.
 *
 * Wraps the trusted transactional RPCs (create/receive/cancel/approve) so the
 * UI never knows how a purchase order, its lines and the incoming-balance
 * recomputation are transacted.
 */

import { callRpc } from "@/lib/api/rpc";

export interface PurchaseLineInput {
  product_id: string;
  variant_id: string | null;
  qty: number;
  unit_cost?: number;
  supplier_sku?: string | null;
}

export interface CreatePurchaseOrderParams {
  supplierId: string;
  locationId: string;
  expectedDate: string | null;
  currency: string;
  notes: string | null;
  supplierReference: string | null;
  shippingCost: number;
  discount: number;
  lines: PurchaseLineInput[];
  idempotencyKey: string;
}

export function createPurchaseOrder(params: CreatePurchaseOrderParams): Promise<string> {
  return callRpc<string>("create_purchase_order", {
    p_supplier_id: params.supplierId,
    p_location_id: params.locationId,
    p_expected_date: params.expectedDate,
    p_currency: params.currency,
    p_notes: params.notes,
    p_supplier_reference: params.supplierReference,
    p_shipping_cost: params.shippingCost,
    p_discount: params.discount,
    p_lines: params.lines,
    p_idempotency_key: params.idempotencyKey,
  });
}

export interface UpdatePurchaseOrderParams {
  poId: string;
  expectedDate: string | null;
  currency: string;
  notes: string | null;
  supplierReference: string | null;
  shippingCost: number;
  discount: number;
  lines: PurchaseLineInput[];
}

/**
 * Apply a draft correction to an existing purchase order. The canonical RPC
 * re-validates every line (MOQ / order multiple / currency authority) and
 * recomputes totals authoritatively — the UI never trusts client totals.
 */
export function updatePurchaseOrder(params: UpdatePurchaseOrderParams): Promise<void> {
  return callRpc("update_purchase_order", {
    p_po_id: params.poId,
    p_expected_date: params.expectedDate,
    p_currency: params.currency,
    p_notes: params.notes,
    p_supplier_reference: params.supplierReference,
    p_shipping_cost: params.shippingCost,
    p_discount: params.discount,
    p_lines: params.lines,
  });
}

export function receivePurchaseOrder(
  poId: string,
  lines: { product_id: string; variant_id: string | null; qty: number }[],
  idempotencyKey?: string,
): Promise<void> {
  return callRpc("receive_purchase_order", {
    p_po_id: poId,
    p_lines: lines,
    p_idempotency_key: idempotencyKey ?? undefined,
  });
}

export function cancelPurchaseOrder(poId: string): Promise<void> {
  return callRpc("cancel_purchase_order", { p_po_id: poId });
}

export function approvePurchaseOrder(poId: string): Promise<void> {
  return callRpc("approve_purchase_order", { p_po_id: poId });
}

export function recomputeIncomingBalances(orgId: string): Promise<void> {
  return callRpc("recompute_incoming_balances", { p_org_id: orgId });
}